import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import AddIcon from '@wix/da-ds/pkg/Icons/16x16/Add';
import { ShippingDestination } from '../../../../redux/types';
import RateRow from '../RateRow';

import s from './ProfileForm.scss';

const MAX_DESTINATIONS = 50;

export interface ProfileFormValues {
  title: string;
  rates: { destinationKey: string; priceUsd: string }[];
}

interface Props {
  destinations: ShippingDestination[];
}

function destinationKey(dest: ShippingDestination): string {
  return `${dest.destinationType ?? ''}:${dest.destinationId ?? ''}`;
}

const ProfileForm: React.FC<Props> = ({ destinations }) => {
  const { t } = useTranslation();
  const titleId = useId();

  const form = useFormContext<ProfileFormValues>();

  const { fields, append, remove } = useFieldArray<{ destinationKey: string; priceUsd: string }>({
    control: form.control,
    name: 'rates',
  });

  const titleError = form.formState.errors.title?.message as string | undefined;

  const rates = form.watch('rates') ?? [];
  const usedKeys = new Set(rates.map(r => r.destinationKey).filter(Boolean));
  const allDestinationOptions = destinations.map(d => ({
    value: destinationKey(d),
    label: d.name,
  }));

  return (
    <div className={s['root']}>
      <div className={s['field']}>
        <Controller
          control={form.control}
          name="title"
          render={({ value, onChange }) => (
            <TextInput
              id={titleId}
              label={t('manageShippingProfiles.editor.title.label')}
              value={value}
              onChange={e => {
                onChange(e.target.value);
              }}
              validationResult={titleError ? 'error' : undefined}
              details={titleError}
              noBottomSpacing
            />
          )}
        />
      </div>
      <div className={s['destinations']}>
        <span className={s['destinations-label']}>
          {t('manageShippingProfiles.editor.destinations.label')}
        </span>
        <div className={s['rate-rows']}>
          {fields.map((field, index) => {
            const currentKey = rates[index]?.destinationKey ?? '';
            const availableOptions = allDestinationOptions.filter(
              opt => opt.value === currentKey || !usedKeys.has(opt.value)
            );
            return (
              <RateRow
                key={field.id}
                index={index}
                defaultDestinationKey={field.destinationKey ?? ''}
                defaultPriceUsd={field.priceUsd ?? ''}
                availableOptions={availableOptions}
                canRemove={fields.length > 1}
                onRemove={() => remove(index)}
              />
            );
          })}
        </div>
        {fields.length < MAX_DESTINATIONS && (
          <TextButton
            size="medium"
            variant="main"
            icon={AddIcon}
            onClick={() => append({ destinationKey: '', priceUsd: '' })}
          >
            {t('manageShippingProfiles.editor.addDestination')}
          </TextButton>
        )}
      </div>
    </div>
  );
};
ProfileForm.displayName = 'ProfileForm';

export default ProfileForm;
